import { filterColumnToMap } from '../../../../utils/utils';
import { DataTableColumn } from '../DataTable.types';

/**
 * Interface for column transformation utilities
 */
export interface ColumnTransformUtils {
    configToIds: (columns: DataTableColumn[]) => string[];
    idsToConfig: (columnIds: string[], masterConfig: DataTableColumn[]) => DataTableColumn[];
    validateColumnIds: (columnIds: string[], masterConfig: DataTableColumn[]) => string[];
    getDefaultColumnIds: (masterConfig: DataTableColumn[]) => string[];
}

/**
 * Converts an array of DataTableColumn configurations to an array of column field IDs
 * Preserves the order of columns as provided
 *
 * @param columns - Array of DataTableColumn configurations
 * @returns Array of column field IDs in the same order
 */
export const configToIds = (columns: DataTableColumn[]): string[] => {
    if (!Array.isArray(columns)) {
        console.warn('configToIds: Expected array of columns, received:', typeof columns);
        return [];
    }

    return columns
        .filter((column) => column && column.field) // Filter out invalid columns
        .map((column) => column.field);
};

/**
 * Converts an array of column field IDs to DataTableColumn configurations
 * Uses the master config to retrieve full column configurations
 * Preserves the order specified in the columnIds array
 *
 * @param columnIds - Array of column field IDs in desired order
 * @param masterConfig - Master configuration containing all available columns
 * @returns Array of DataTableColumn configurations in the specified order
 */
export const idsToConfig = (columnIds: string[], masterConfig: DataTableColumn[]): DataTableColumn[] => {
    if (!Array.isArray(columnIds)) {
        console.warn('idsToConfig: Expected array of column IDs, received:', typeof columnIds);
        return masterConfig || [];
    }

    if (!Array.isArray(masterConfig)) {
        console.warn('idsToConfig: Expected array of master config, received:', typeof masterConfig);
        return [];
    }

    // Create a lookup map for O(1) access to column configurations
    // Prioritize visible columns (show === undefined || show === true) over hidden ones
    // This handles cases where multiple columns have the same field but different show values
    const masterConfigMap = new Map<string, DataTableColumn>();
    masterConfig.forEach((column) => {
        if (column && column.field) {
            filterColumnToMap(masterConfigMap, column);
        }
    });

    // Convert IDs to configurations in the specified order
    const result = columnIds
        .filter((id) => typeof id === 'string' && id.trim() !== '') // Filter out invalid IDs
        .map((id) => masterConfigMap.get(id))
        .filter((column): column is DataTableColumn => column !== undefined); // Type guard to remove undefined values

    // Log warning if some column IDs were not found
    const foundIds = result.map((column) => column.field);
    const missingIds = columnIds.filter((id) => !foundIds.includes(id));
    if (missingIds.length > 0) {
        console.warn('idsToConfig: Some column IDs were not found in master config:', missingIds);
    }

    return result;
};

/**
 * Validates an array of column IDs against the master configuration
 * Returns only the valid column IDs that exist in the master config
 *
 * @param columnIds - Array of column field IDs to validate
 * @param masterConfig - Master configuration to validate against
 * @returns Array of valid column field IDs
 */
export const validateColumnIds = (columnIds: string[], masterConfig: DataTableColumn[]): string[] => {
    if (!Array.isArray(columnIds) || !Array.isArray(masterConfig)) {
        console.warn('validateColumnIds: Expected arrays for both columnIds and masterConfig, received:', {
            columnIds: typeof columnIds,
            masterConfig: typeof masterConfig,
        });
        return [];
    }

    // Create a set of valid column IDs for O(1) lookup
    const validFieldsSet = new Set<string>();
    masterConfig.forEach((column) => {
        if (column && column.field) {
            validFieldsSet.add(column.field);
        }
    });

    // Filter out invalid or non-existent column IDs
    const validIds = columnIds.filter((id) => {
        if (typeof id !== 'string' || id.trim() === '') {
            return false;
        }
        return validFieldsSet.has(id);
    });

    // Log warning if some IDs were filtered out
    if (validIds.length !== columnIds.length) {
        const invalidIds = columnIds.filter((id) => !validIds.includes(id));
        console.warn('validateColumnIds: Some column IDs were invalid or not found:', invalidIds);
    }

    return validIds;
};

/**
 * Gets the default column IDs from the master configuration
 * Returns all column field IDs in their original order
 *
 * @param masterConfig - Master configuration containing all available columns
 * @returns Array of all column field IDs in their default order
 */
export const getDefaultColumnIds = (masterConfig: DataTableColumn[]): string[] => {
    if (!Array.isArray(masterConfig)) {
        console.warn('getDefaultColumnIds: Expected array of master config, received:', typeof masterConfig);
        return [];
    }

    return masterConfig
        .filter((column) => column && column.field) // Filter out invalid columns
        .map((column) => column.field);
};

/**
 * Utility object containing all column transformation functions
 * Useful for passing around as a single dependency
 */
export const columnTransformUtils: ColumnTransformUtils = {
    configToIds,
    idsToConfig,
    validateColumnIds,
    getDefaultColumnIds,
};

/**
 * Type definitions for column customization state management
 */

export interface ColumnCustomizationState {
    open: boolean;
    sourceKey: string;
    selectedColumns: string[];
    tempSelectedColumns: string[];
    isModified: boolean;
}

/**
 * Interface for filtered view data that includes column configuration
 */
export interface FilteredViewData {
    filters: Record<string, any>;
    columns?: string[];
    viewName: string;
    viewId: string;
}

/**
 * Interface for saving view payload with column data
 */
export interface SaveViewPayload {
    viewName: string;
    filters: Record<string, any>;
    columns: string[];
    source: string;
    organizationId: string;
}

/**
 * Interface for view response from API
 */
export interface ViewResponse {
    viewId: string;
    viewName: string;
    filters: Record<string, any>;
    columns?: string[];
    createdAt: string;
    updatedAt: string;
}
